import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CounselorWorkTimeResponse, counselorsApi } from "@/api";
import { nameof } from "@/common/helper/commonHelper";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { CounselorWorkTimeFormValues, WorkTimeAllocationRowValues } from "../CounselorWorkTime.types";
import { counselorWorkTimeValidationSchema } from "../CounselorWorkTime.utils";

function buildAllocations(response: CounselorWorkTimeResponse | undefined): WorkTimeAllocationRowValues[] {
  return (response?.allocations ?? []).map((a) => ({
    id: a.id,
    workArea: a.workArea,
    team: a.team,
    validFrom: a.validFrom,
    validTo: a.validTo ?? null,
    hours: a.hours,
  }));
}

export function useCounselorWorkTime(counselorId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();
  const { teams, workAreas } = useDropdownOptions();

  const { data } = useQuery<CounselorWorkTimeResponse>({
    queryKey: [QueryKey.CounselorWorkTime, counselorId],
    queryFn: () => counselorsApi.getCounselorWorkTime({ id: counselorId }),
    meta: { loadingScope: "pageContent" },
  });

  const saveMutation = useMutation({
    mutationFn: (values: CounselorWorkTimeFormValues) =>
      counselorsApi.saveCounselorWorkTime({
        id: counselorId,
        saveCounselorWorkTimeRequest: {
          weeklyHours: values.weeklyHours ?? 0,
          allocations: values.allocations
            .filter((a): a is WorkTimeAllocationRowValues & { validFrom: Date } => a.validFrom !== null)
            .map((a) => ({
              id: a.id ?? null,
              workArea: a.workArea,
              team: a.team,
              hours: a.hours ?? 0,
              validFrom: a.validFrom,
              validTo: a.validTo,
            })),
        },
      }),
  });

  const validationSchema = useMemo(() => counselorWorkTimeValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<CounselorWorkTimeFormValues>({
    initialValues: {
      weeklyHours: data?.weeklyHours ?? 0,
      allocations: buildAllocations(data),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await saveMutation.mutateAsync(values);
      showSuccess(t("counselors.workTime.saveSuccess"));
      void queryClient.invalidateQueries({ queryKey: [QueryKey.CounselorWorkTime, counselorId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Counselors] });
    },
  });

  const handleAddAllocation = () => {
    void formik.setFieldValue(nameof<CounselorWorkTimeFormValues>("allocations"), [
      ...formik.values.allocations,
      { workArea: "", team: "", validFrom: null, validTo: null, hours: null },
    ]);
  };

  const handleDeleteAllocation = (index: number) => {
    void formik.setFieldValue(
      nameof<CounselorWorkTimeFormValues>("allocations"),
      formik.values.allocations.filter((_, i) => i !== index)
    );
  };

  return {
    formik,
    teamOptions: teams,
    workAreaOptions: workAreas,
    handleAddAllocation,
    handleDeleteAllocation,
  };
}
